import { FC, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { useSettings } from "@framework/provider-settings";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import { Trends } from "../../components/analytics";
import { StateAndForecast } from "../../components/state-and-forecast";
import { Train } from "../../components/train";
import { Tables } from "../../components/tables";
import { sanitizeSchemePageView } from "../../utils/scheme-page-view";
import { AccordionRoot, AccordionRowContainer, COLLAPSED_WIDTH, COLLAPSED_HEIGHT, GAP_SIZE } from "./styled";

const keysMapper: Record<string, keyof IUser["interfaceSettings"]> = {
  "1": "showTrainScheme",
  "2": "showStatusAndForecasting",
  "3": "showTableData",
  "4": "showMeasureData",
};

const componentMapper: Record<string, FC> = {
  "1": Train,
  "2": StateAndForecast,
  "3": Tables,
  "4": Trends,
};

const standaloneComponentMapper: Record<string, FC> = {
  train: Train,
  stateAndForecast: StateAndForecast,
  tables: Tables,
  trends: Trends,
};

const ComponentWrapper = ({ Component, isNarrow }: { Component: FC<{ isNarrow?: boolean }>; isNarrow?: boolean }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Component isNarrow={isNarrow} />
    </Box>
  );
};

export const TrainScheme: FC = () => {
  const settings = useSettings();
  const { profile } = useUser<IUser>();
  const [searchParams] = useSearchParams();
  const schemeState = settings.getSchemeState();
  const onlyPage = searchParams.get("onlyPage") === "true";
  const currentComponent = searchParams.get("component");

  const schemeStateMapper: Record<string, keyof typeof schemeState> = {
    "1": "train",
    "2": "stateAndForecast",
    "3": "tables",
    "4": "analytics",
  };

  const schemeColumnCount = settings.getSchemeColumnCount();
  const colsPerRow = Number(schemeColumnCount);

  const componentsOrder = useMemo(() => {
    return sanitizeSchemePageView(profile.interfaceSettings.schemePageView).filter(
      id => profile.interfaceSettings[keysMapper[id]],
    );
  }, [profile.interfaceSettings]);

  const expandedStates = useMemo(() => {
    return componentsOrder.map(id => {
      const stateKey = schemeStateMapper[id];
      return stateKey ? schemeState[stateKey] : false;
    });
  }, [componentsOrder, schemeState, schemeStateMapper]);

  const accordionRows = useMemo(() => {
    const rows: { id: string; isExpanded: boolean; Component: FC }[][] = [];

    for (let i = 0; i < componentsOrder.length; i += colsPerRow) {
      const rowItems = componentsOrder.slice(i, i + colsPerRow).map((id, idx) => {
        const index = i + idx;
        return {
          id,
          isExpanded: expandedStates[index],
          Component: componentMapper[id],
        };
      });
      rows.push(rowItems);
    }

    return rows;
  }, [componentsOrder, colsPerRow, expandedStates]);

  // Расчет высоты рядов аккордеона
  const getRowDynamicHeight = useCallback(
    (row: { isExpanded: boolean }[]) => {
      const hasExpandedItem = row.some(item => item.isExpanded);

      if (!hasExpandedItem) {
        return {
          minHeight: `${COLLAPSED_HEIGHT}px`,
          height: `${COLLAPSED_HEIGHT}px`,
          flex: "0 0 auto",
        };
      }

      const totalRows = accordionRows.length;
      const expandedRows = accordionRows.filter(r => r.some(item => item.isExpanded)).length;
      const collapsedRowsHeight = (totalRows - expandedRows) * COLLAPSED_HEIGHT;
      const gapsHeight = (totalRows - 1) * GAP_SIZE;
      const availableSpace = `calc(100% - ${collapsedRowsHeight}px - ${gapsHeight}px)`;
      const flexBasis = `calc(${availableSpace} / ${expandedRows})`;

      return {
        minHeight: "0",
        flex: `1 1 ${flexBasis}`,
        height: "auto",
      };
    },
    [accordionRows],
  );

  // Расчет ширины элементов в ряду аккордеона
  const calculateItemWidths = useCallback((row: { id: string; isExpanded: boolean; Component: FC }[]) => {
    const allCollapsed = row.every(item => !item.isExpanded);

    if (allCollapsed) {
      // Если все свёрнуты - делим поровну
      const equalWidth = `calc((100% - ${(row.length - 1) * GAP_SIZE}px) / ${row.length})`;
      return row.map(item => ({ ...item, width: equalWidth, flex: "1 1 0%" }));
    }

    // Если есть развернутые - компактные свернутые
    const expandedCount = row.filter(item => item.isExpanded).length;
    const collapsedSpace = (row.length - expandedCount) * COLLAPSED_WIDTH;
    const expandedWidth = `calc((100% - ${collapsedSpace}px - ${(row.length - 1) * GAP_SIZE}px) / ${expandedCount})`;

    return row.map(item => ({
      ...item,
      width: item.isExpanded ? expandedWidth : `${COLLAPSED_WIDTH}px`,
      flex: item.isExpanded ? "1 1 0%" : "0 0 auto",
    }));
  }, []);

  const renderAccordionMode = () => (
    <AccordionRoot sx={{ overflow: "hidden" }}>
      {accordionRows.map((row, rowIndex) => {
        const rowStyles = getRowDynamicHeight(row);

        const itemsWithWidth = calculateItemWidths(row);

        return (
          <AccordionRowContainer key={`row-${rowIndex}`} sx={rowStyles}>
            {itemsWithWidth.map(({ id, isExpanded, Component, width, flex }) => {
              const isNarrow = width === `${COLLAPSED_WIDTH}px`;
              return (
                <Box
                  key={id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width,
                    flex,
                    height: isExpanded ? "auto" : `${COLLAPSED_HEIGHT}px`,
                    minHeight: isExpanded ? 0 : COLLAPSED_HEIGHT,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                  }}
                >
                  <ComponentWrapper Component={Component} isNarrow={isNarrow} />
                </Box>
              );
            })}
          </AccordionRowContainer>
        );
      })}
    </AccordionRoot>
  );

  if (onlyPage && currentComponent && standaloneComponentMapper[currentComponent]) {
    const StandaloneComponent = standaloneComponentMapper[currentComponent];

    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
        <ComponentWrapper Component={StandaloneComponent} />
      </Box>
    );
  }

  return renderAccordionMode();
};
